// src/components/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { Loader2, CheckCircle2, Lock } from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';
import SEO from './SEO';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract parameters from URL - Firebase redirects to: /auth?mode=resetPassword&oobCode=...&returnTo=mobile
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  
  // Extract returnTo from multiple sources and store in state/sessionStorage to preserve it
  // Firebase might reconstruct the URL during redirect, so we need robust extraction
  const returnToFromUrl = searchParams.get('returnTo') || new URLSearchParams(window.location.search).get('returnTo');
  const [returnTo, setReturnTo] = useState(() => {
    // Try to get from sessionStorage first (preserved from previous navigation)
    const savedReturnTo = sessionStorage.getItem('passwordResetReturnTo');
    if (savedReturnTo) {
      return savedReturnTo;
    }
    // Otherwise use value from URL
    return returnToFromUrl;
  });
  
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Extract returnTo from URL directly as fallback (in case searchParams doesn't capture it)
    const urlParams = new URLSearchParams(window.location.search);
    const returnToFromUrl = urlParams.get('returnTo');
    const returnToFromSearchParams = searchParams.get('returnTo');
    
    // Use the most reliable source (prefer URL directly, then searchParams, then existing state)
    const finalReturnTo = returnToFromUrl || returnToFromSearchParams || returnTo;
    
    // Update state and sessionStorage if we found returnTo in URL
    if (finalReturnTo && finalReturnTo !== returnTo) {
      setReturnTo(finalReturnTo);
      sessionStorage.setItem('passwordResetReturnTo', finalReturnTo);
    } else if (returnTo) {
      // Preserve existing returnTo in sessionStorage
      sessionStorage.setItem('passwordResetReturnTo', returnTo);
    }
    
    // Log URL parameters for debugging
    console.log('[ResetPassword] URL params:', {
      mode,
      oobCode: oobCode ? `${oobCode.substring(0, 20)}...` : null,
      returnTo: returnTo, // From state
      returnToFromSearchParams: returnToFromSearchParams, // From React Router
      returnToFromUrl: returnToFromUrl, // From window.location.search directly
      finalReturnTo: finalReturnTo,
      fullUrl: window.location.href,
      search: window.location.search,
      pathname: window.location.pathname,
      hash: window.location.hash,
      sessionStorage: sessionStorage.getItem('passwordResetReturnTo')
    });

    // Check if this is a password reset request
    if (mode !== 'resetPassword') {
      console.warn('[ResetPassword] Invalid mode:', mode);
      setError('Invalid reset link. Please request a new password reset.');
      setLoading(false);
      return;
    }

    if (!oobCode) {
      console.warn('[ResetPassword] Missing oobCode in URL');
      setError('Missing reset code. Please use the complete link from your email. If you clicked the link multiple times, please request a new password reset.');
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [mode, oobCode, returnTo, searchParams]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }

    setResetting(true);
    setError(null);

    try {
      console.log('[ResetPassword] Attempting password reset with oobCode:', oobCode ? `${oobCode.substring(0, 20)}...` : null);
      
      // Use Firebase's confirmPasswordReset to reset the password
      await confirmPasswordReset(auth, oobCode, password);
      
      console.log('[ResetPassword] Password reset successful');
      
      // Check for returnTo parameter - check multiple sources to ensure we get it
      // Priority: state > sessionStorage > URL (searchParams) > URL (window.location.search)
      const returnToFromState = returnTo;
      const returnToFromSession = sessionStorage.getItem('passwordResetReturnTo');
      const returnToFromSearchParams = searchParams.get('returnTo');
      const returnToFromUrl = new URLSearchParams(window.location.search).get('returnTo');
      const returnToParam = returnToFromState || returnToFromSession || returnToFromSearchParams || returnToFromUrl;
      
      console.log('[ResetPassword] Checking returnTo for redirect:', {
        returnToFromState,
        returnToFromSession,
        returnToFromSearchParams,
        returnToFromUrl,
        finalReturnTo: returnToParam,
        willRedirectToMobile: returnToParam === 'mobile'
      });
      
      setSuccess(true);
      
      // Check if should redirect to mobile app
      if (returnToParam === 'mobile') {
        console.log('[ResetPassword] Redirecting to mobile app via deep link: epiphoricmobile://auth?passwordReset=success');
        // Clear sessionStorage after use
        sessionStorage.removeItem('passwordResetReturnTo');
        // Redirect to mobile app deep link immediately (no delay)
        window.location.href = 'epiphoricmobile://auth?passwordReset=success';
      } else {
        console.log('[ResetPassword] Web flow - redirecting to /auth after 3 seconds');
        // Clear sessionStorage
        sessionStorage.removeItem('passwordResetReturnTo');
        // Normal web flow - redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    } catch (err) {
      console.error('[ResetPassword] Password reset error:', {
        code: err.code,
        message: err.message,
        oobCode: oobCode ? `${oobCode.substring(0, 20)}...` : null
      });
      
      let errorMessage = 'Failed to reset password. ';
      
      if (err.code === 'auth/expired-action-code') {
        errorMessage += 'The reset link has expired (links expire after 1 hour). Please request a new password reset.';
      } else if (err.code === 'auth/invalid-action-code') {
        errorMessage += 'The reset link is invalid or has already been used. Each link can only be used once. Please request a new password reset.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage += 'Password is too weak. Please choose a stronger password.';
      } else {
        errorMessage += `Error: ${err.message || 'Unknown error'}. Please try again or request a new reset link.`;
      }
      
      setError(errorMessage);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SEO 
          title="Reset Password - Epiphoric"
          description="Reset your Epiphoric password"
        />
        <div className="flex flex-col min-h-screen relative">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Validating reset link...</p>
            </div>
          </div>
          <Footer variant="ghost" />
        </div>
      </>
    );
  }

  if (success) {
    // Check returnTo for success message (same logic as redirect check)
    const returnToForMessage = returnTo || sessionStorage.getItem('passwordResetReturnTo') || searchParams.get('returnTo') || new URLSearchParams(window.location.search).get('returnTo');
    const isMobile = returnToForMessage === 'mobile';
    
    return (
      <>
        <SEO 
          title="Password Reset Successful - Epiphoric"
          description="Your password has been reset successfully"
        />
        <div className="flex flex-col min-h-screen relative">
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md w-full text-center">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Password Reset Successful!
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  Your password has been reset successfully. {isMobile ? 'Redirecting to mobile app...' : 'Redirecting to login...'}
                </p>
              </div>
              {!isMobile && (
                <a
                  href="/auth"
                  className="text-primary hover:underline"
                >
                  Go to login page
                </a>
              )}
            </div>
          </div>
          <Footer variant="ghost" />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Reset Password - Epiphoric"
        description="Reset your Epiphoric password"
      />
      <div className="flex flex-col min-h-screen relative">
        <div className="grid grid-cols-1 md:grid-cols-2 flex-1 min-h-0">
          {/* Left Side: The Vibe */}
          <div className="bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col justify-between items-start p-8 md:p-12 lg:p-16 relative">
            <div className="mb-8">
              <Logo size="large" />
            </div>
            
            <div className="flex-1 flex flex-col justify-center w-full">
              <h1 className="text-white text-5xl font-bold mb-4 leading-tight">
                Reset Password.
              </h1>
              <p className="text-slate-300 text-lg mt-4 leading-relaxed max-w-lg">
                Set a new password for your Epiphoric account.
              </p>
            </div>

            <div className="absolute bottom-24 left-8 md:left-12 lg:left-16 flex items-center gap-2 text-indigo-200 text-sm">
              <Lock className="w-4 h-4" />
              <span>Secure & Encrypted. Your ideas are private.</span>
            </div>
          </div>

          {/* Right Side: The Action */}
          <div className="bg-white dark:bg-slate-950 flex flex-col justify-center items-center p-8 md:p-12 overflow-y-auto min-h-0">
            <div className="w-full max-w-md">
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
                Reset Password
              </h2>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
                  {error}
                </div>
              )}

              {!loading && (
                <form onSubmit={handleResetPassword} className="w-full">
                  <div className="mb-4">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                      minLength={6}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={resetting}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {resetting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Resetting Password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              )}

              <div className="mt-6 text-center">
                <a
                  href="/auth"
                  className="text-sm text-primary hover:underline"
                >
                  Back to login
                </a>
              </div>
            </div>
          </div>
        </div>
        <Footer variant="ghost" />
      </div>
    </>
  );
}

export default ResetPassword;

